import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

const buttonVariants = cva(
	'inline-flex items-center justify-center rounded-xxs gap-2 text-sm font-medium transition-[colors,_opacity] duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 select-none',
	{
		variants: {
			variant: {
				default:
					'bg-brand-earth text-brand-earth-foreground hover:bg-brand-earth/80',
				destructive:
					'border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground',
				warning:
					'border border-status-warning text-status-warning hover:bg-status-warning hover:text-white',
				outline:
					'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-10 px-4 py-2',
				wide: 'px-24 py-5',
				xs: 'h-6 w-6',
				sm: 'h-9 px-3',
				lg: 'h-11 px-8',
				pill: 'px-12 py-3 leading-3',
				icon: 'h-9 w-9',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
	confirm?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, variant, confirm, size, asChild = false, children, ...props },
		ref,
	) => {
		const [isConfirming, setIsConfirming] = React.useState(false)
		const Comp = asChild ? Slot : 'button'

		return (
			<Comp
				className={cn(
					buttonVariants({
						variant,
						// size: confirm && isConfirming ? 'default' : (size ?? undefined),
						size,
						className,
					}),
				)}
				ref={ref}
				{...props}
				onClick={(e) => {
					if (confirm && !isConfirming) {
						e.preventDefault()
						setIsConfirming(true)

						setTimeout(() => {
							setIsConfirming(false)
						}, 5000)

						return
					}

					if (confirm && isConfirming) {
						setIsConfirming(false)
						props.onClick?.(e)
					}

					if (!confirm) {
						props.onClick?.(e)
					}
				}}
			>
				{isConfirming ? '¿Estás seguro?' : children}
			</Comp>
		)
	},
)
Button.displayName = 'Button'

const isMac =
	typeof window !== 'undefined'
		? navigator.platform.toUpperCase().indexOf('MAC') >= 0
		: false

const ShortcutKey = ({ children }: { children: string }): JSX.Element => {
	const className =
		'inline-flex items-center justify-center w-fit h-5 p-1 text-[0.625rem] rounded font-semibold leading-none border border-neutral-200 text-neutral-500 border-b-2'

	if (children === 'Mod') {
		return <kbd className={className}>{isMac ? '⌘' : 'Ctrl'}</kbd> // ⌃
	}

	if (children === 'Shift') {
		return <kbd className={className}>⇧</kbd>
	}

	if (children === 'Alt') {
		return <kbd className={className}>{isMac ? '⌥' : 'Alt'}</kbd>
	}

	return <kbd className={className}>{children}</kbd>
}

const ButtonWithTooltip = ({
	tooltip,
	shortcut,
	children,
}: {
	tooltip: string
	shortcut?: string[]
	children: React.ReactNode
}) => (
	<Tooltip>
		<TooltipTrigger asChild>{children}</TooltipTrigger>
		<TooltipContent>
			<span>{tooltip}</span>
			{shortcut && (
				<span className="ml-xxs inline-flex items-center gap-px">
					{shortcut.map((s) => (
						<ShortcutKey key={s}>{s}</ShortcutKey>
					))}
				</span>
			)}
		</TooltipContent>
	</Tooltip>
)

export { Button, ButtonWithTooltip, buttonVariants }
